import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button as ButtonBase, Popover, Typography, TextField } from '@mui/material';
import FilterIcon from '../../assets/icons/filter-icon.svg';
import Button from '../../common/button/Button';
import Select from '../../common/Select/Select';
import { ImageIcon } from '../../utils/UtilsComponent';
import { useAppDispatch, useAppSelector } from '../../state/store';
import { setFilters, resetFilters, selectReportFilters, selectReports } from '../../state/modules/report/reportReducer';
import { useGetGatewayTypesQuery } from '../../services/gateway.service';
import { useGetProvincesQuery } from '../../services/geography.service';

const FilterBox = () => {
  const dispatch = useAppDispatch();
  const currentFilters = useAppSelector(selectReportFilters);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { data: provincesData = [] } = useGetProvincesQuery();
  const [filters, setLocalFilters] = useState({
    ...currentFilters,
    from: currentFilters.from || '',
    to: currentFilters.to || '',
  });

  useEffect(() => {
    setLocalFilters({
      ...currentFilters,
      from: currentFilters.from || '',
      to: currentFilters.to || '',
    });
  }, [currentFilters]);

  const { data: devicetypeData, isLoading: isDevicetypeLoading } = useGetGatewayTypesQuery(null);

  const deviceTypeOptions = useMemo(() => {
    return devicetypeData
      ? [
          { value: 'all', label: 'Tất cả' },
          ...devicetypeData.map((org: any) => ({
            value: org.id,
            label: org.name,
          })),
        ]
      : [{ value: 'all', label: 'Tất cả' }];
  }, [devicetypeData]);

  const diaryTypeOptions = useMemo(
    () => [
      { value: 'all', label: 'Tất cả' },
      { value: 'alert', label: 'Cảnh báo' },
      { value: 'warning', label: 'Lưu ý' },
      { value: 'disconnected', label: 'Mất kết nối' },
    ],
    []
  );
  const toSlug = (str?: string) => {
  if (!str) return ''; 
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
};

  
  const provinceOptions = useMemo(
  () => [
    { value: 'all', label: 'Tất cả' },
    ...provincesData.map((item: any) => {
      const label =  item.fullName || item.name || '';
      return {
        label,
        value: toSlug(label),
        code: item.code,
      };
    }),
  ],
  [provincesData]
);



  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleApply = () => {
    dispatch(
      setFilters({
        ...filters,
        from: filters.from ? Math.floor(new Date(filters.from).getTime() / 1000) : '',
        to: filters.to ? Math.floor(new Date(filters.to).getTime() / 1000) : '',
      })
    );
    handleClose();
  };

  const handleReset = () => {
    dispatch(resetFilters());
    setLocalFilters({
      supervisor: 'all',
      deviceType: 'all',
      diaryType: 'all',
      province: 'all',
      from: '',
      to: '',
    });
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'filter-popover' : undefined;

  return (
    <Box>
      <ButtonBase aria-describedby={id} onClick={handleClick}>
        <ImageIcon image={FilterIcon} />
        <Typography sx={{ marginLeft: '8px' }}>Bộ lọc</Typography>
      </ButtonBase>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: '20px 20px 32px',
            width: 500,
            borderRadius: '12px',
            boxSizing: 'border-box',
          },
        }}
      >
        <Typography fontWeight={700} fontSize={20} mb={2}>
          Bộ lọc
        </Typography>

        <Box display="flex" flexWrap="wrap" justifyContent="space-between" gap={2}>
          <Box style={{ width: 222 }}>
            <Typography fontSize={14} fontWeight={400} mb={0.5}>
              Từ ngày
            </Typography>
            <TextField
              type="datetime-local"
              size="small"
              value={filters.from || ''}
              onChange={(e) => setLocalFilters({ ...filters, from: e.target.value })}
              fullWidth
              inputProps={{ style: { fontSize: 14 } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  height: '44px'
                },
              }}
            />
          </Box>
          <Box style={{ width: 222 }}>
            <Typography fontSize={14} fontWeight={400} mb={0.5}>
              Đến ngày
            </Typography>
            <TextField
              type="datetime-local"
              size="small"
              value={filters.to || ''}
              onChange={(e) => setLocalFilters({ ...filters, to: e.target.value })}
              fullWidth
              inputProps={{ style: { fontSize: 14 } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  height: '44px'
                },
              }}
            />
          </Box>
          <Select
            topLable="Loại thiết bị"
            data={deviceTypeOptions}
            selected={filters.deviceType}
            setSelected={(value) => setLocalFilters({ ...filters, deviceType: value })}
            style={{ width: 222 }}
            disabled={isDevicetypeLoading}
          />
          <Select
            topLable="Loại nhật ký"
            data={diaryTypeOptions}
            selected={filters.diaryType}
            setSelected={(value) => setLocalFilters({ ...filters, diaryType: value })}
            style={{ width: 222 }}
          />
          <Select
            topLable="Tỉnh/Thành phố"
            data={provinceOptions}
            selected={filters.province || 'all'}
            setSelected={(value) => setLocalFilters({ ...filters, province: value })}
            style={{ width: 222 }}
            disabled={isDevicetypeLoading}
          />
        </Box>
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" style={{ width: 131 }} onClick={handleReset}>
            Xoá bộ lọc
          </Button>
          <Button variant="contained" style={{ width: 131 }} onClick={handleApply}>
            Áp dụng
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default FilterBox;